import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { BrowserService } from '../../../entities/browser.service';
import { Category } from '../../../entities/category';
import { Option } from '../../../entities/option';
import { Pano } from '../../../entities/pano';
import { PanoService } from '../../../entities/pano.service';
import { Tour } from '../../../entities/tour';
import { View } from '../../../entities/view';
import { SettingsService } from '../../../services/settings.service';
import { GoogleTagManagerService } from '../../../utility/helpers/google-tag-manager';
import { AnimationState, heightExpandCollapseAnimation } from '../../animations';
import { ResetConfirmDialogComponent } from '../../reset-confirm-dialog/reset-confirm-dialog.component';
import { MenuAccordionItemVM, MenuAccordionVM } from './menu-accordion/menu-accordion-vm';
import { SettingsCategoryTitle, sortByOrder, sortBySortOrder } from '@ml/common';
import { KRPanoService } from '../../../entities/krpano.service';

@Component({
  selector: 'basic-menu',
  templateUrl: './basic-menu.component.html',
  styleUrls: ['./basic-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [heightExpandCollapseAnimation(500)]
})
export class BasicMenuComponent implements OnInit, OnChanges {
  @Input() pano: Pano;
  @Input() tour: Tour;
  @Input() view: View;
  @Input() isControlsMenuOpen = true;
  @Output() toggleOpen = new EventEmitter<boolean>();
  @Output() viewSelect = new EventEmitter<number>();
  @Output() checkValidCombo = new EventEmitter<boolean>();
  disableResetButton = false;
  isHamburgerMenuOpen = false;
  isCompactScreen = false;
  hasGyroscope = false;
  showOptionsCounter = true;
  hideHamburgerMenu = false;
  viewGroupAccordions: MenuAccordionVM[] = [];
  tourAccordion: MenuAccordionVM = null;
  showCarrotMessage = false;
  hasMoreThanOneOption = true;
  showToursInMenu = false;
  toursMenuName = '';

  openViewGroupCategoryTracker = false;

  constructor(
    private panoService: PanoService,
    private matDialog: MatDialog,
    private browserService: BrowserService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService,
    private krPanoService: KRPanoService
  ) {}

  ngOnInit() {
    this.browserService.compactScreen$.subscribe(this.handleCompactScreenChange);

    this.showOptionsCounter = this.settingsService.get(
      'ShowOptionsCounter',
      SettingsCategoryTitle.PanoViewerTheme
    );

    this.hideHamburgerMenu =
      this.route.snapshot.queryParamMap.get('embedded')?.toLowerCase() === 'true';

    const categoriesOpenOnLoad = this.settingsService.get(
      'CategoriesOpenOnLoad',
      SettingsCategoryTitle.PanoViewerTheme
    );

    for (const category of this.view.Categories) {
      category.IsOpen = categoriesOpenOnLoad;
      this.setAnimationState(category);
    }

    for (const viewAccordion of this.viewGroupAccordions) {
      viewAccordion.IsOpen = categoriesOpenOnLoad;
      this.setAnimationState(viewAccordion);

      for (const item of viewAccordion.ChildItems) {
        item.IsSelected = item.Id === this.view.ViewId;
      }
    }

    this.showCarrotMessage = !this.isControlsMenuOpen;
    this.hasMoreThanOneOption = this.viewHasMoreThanOneOption(this.view);
    if (!this.hasMoreThanOneOption) {
      this.toggleOpen.emit(false);
    }

    this.showToursInMenu =
      this.pano.Settings.MenuSettings.ShowToursInMenu &&
      this.view.Settings.ShowTourGroupsWhenEnabled;

    this.toursMenuName = this.pano.Settings.MenuSettings.ToursMenuName;
    this.tourAccordion = MenuAccordionVM.CreateFromTours(this.toursMenuName, this.pano.Tours);
    this.tourAccordion.IsOpen = categoriesOpenOnLoad;

    for (const item of this.tourAccordion.ChildItems) {
      item.IsSelected = item.Id === this.tour.TourId;
    }
  }

  ngOnChanges() {
    this.viewGroupAccordions = this.view.ViewGroupsAsSource.map(vg =>
      MenuAccordionVM.CreateFromViewGroup(vg, this.panoService.getAllViews())
    ).sort(sortBySortOrder);

    for (const viewAccordion of this.viewGroupAccordions) {
      viewAccordion.IsOpen = this.openViewGroupCategoryTracker;
      this.setAnimationState(viewAccordion);

      for (const item of viewAccordion.ChildItems) {
        item.IsSelected = item.Id === this.view.ViewId;
      }
    }

    const categoriesOpenOnLoad = this.settingsService.get(
      'CategoriesOpenOnLoad',
      SettingsCategoryTitle.PanoViewerTheme
    );

    this.view.Categories = this.view.Categories.map(cat => {
      cat.IsOpen = categoriesOpenOnLoad;
      this.setAnimationState(cat);

      cat.Options = cat.Options.map(x => Object.assign(new Option(), x));
      return cat;
    });

    this.hasMoreThanOneOption = this.viewHasMoreThanOneOption(this.view);
    if (!this.hasMoreThanOneOption) {
      this.toggleOpen.emit(false);
    }

    this.showToursInMenu =
      this.pano.Settings.MenuSettings.ShowToursInMenu &&
      this.view.Settings.ShowTourGroupsWhenEnabled;

    this.checkForDisabledOptions();
    this.cdr.markForCheck();

    setTimeout(() => {
      this.checkForDisabledOptions();
      this.cdr.markForCheck();
    }, 1000);
  }

  viewHasMoreThanOneOption(view: View): boolean {
    return view.Categories.some(c => c.Options.length > 1);
  }

  handleCompactScreenChange = (isCompact: boolean) => {
    // if transitioning down to to compact then hide side menu
    if (isCompact !== this.isCompactScreen && isCompact) this.isControlsMenuOpen = false;
    this.isCompactScreen = isCompact;
    this.cdr.markForCheck();
  };

  checkForDisabledOptions() {
    const selectedOptionIds = new Array<number>();

    for (const category of this.view.Categories) {
      const option = category.Options.find(x => x.IsSelected);
      if (option) {
        selectedOptionIds.push(option.OptionId);
      }
    }

    let tempOptionIds = [...selectedOptionIds];

    let categoryIndex = 0;
    for (const category of this.view.Categories) {
      for (const option of category.Options) {
        if (!option.IsSelected) {
          tempOptionIds[categoryIndex] = option.OptionId;
          const optionCombo = this.view.getOptionCombinationByOptionIds(tempOptionIds);
          if (optionCombo) {
            option.enabled = this.krPanoService.sceneExistsInTourXML(optionCombo.SceneName);
          } else {
            option.enabled = false;
          }
        }
      }

      category.Options = category.Options.map(x => new Option(x, x.IsSelected));
      tempOptionIds = [...selectedOptionIds];
      ++categoryIndex;
    }
  }

  toggleHamburgerMenu(isOpen: boolean) {
    this.isHamburgerMenuOpen = isOpen;

    if (this.isCompactScreen && this.isHamburgerMenuOpen) {
      this.toggleControlMenu(false);
    }
  }

  toggleControlMenu(forceValue?: boolean) {
    this.isControlsMenuOpen = forceValue !== undefined ? forceValue : !this.isControlsMenuOpen;

    if (this.isCompactScreen && this.isControlsMenuOpen) {
      this.isHamburgerMenuOpen = false;
    }

    this.showCarrotMessage = !this.isControlsMenuOpen;
    this.toggleOpen.emit(this.isControlsMenuOpen);
  }

  closeCarrotMessage() {
    this.showCarrotMessage = false;
  }

  toggleCategoryMenu(category: Category | MenuAccordionVM) {
    category.IsOpen = !category.IsOpen;
    this.setAnimationState(category);
  }

  private setAnimationState(category: Category | MenuAccordionVM) {
    if (category instanceof MenuAccordionVM) {
      this.openViewGroupCategoryTracker = category.IsOpen;
    }

    if (category.IsOpen) {
      category.AnimationState = AnimationState.ExpandedHeight;
    } else {
      category.AnimationState = AnimationState.CollapsedHeight;
    }
  }

  selectOption(option: Option, category: Category) {
    if (option.IsSelected || !option.enabled) {
      return;
    }

    if (category.Options) {
      category.Options = category.Options.map(
        opt => new Option(opt, opt.OptionId === option.OptionId)
      );

      this.checkForDisabledOptions();

      this.gtmService.addEvent('OptionOn', 'Option', option.OptionId.toString(), this.view.ViewId);

      try {
        this.panoService.updateSceneByView(this.view);
        this.checkValidCombo.emit(true);
      } catch (error) {
        this.checkValidCombo.emit(false);
      }

      this.disableResetButton = this.isDefault();
      this.cdr.markForCheck();
    }
  }

  private isDefault(): boolean {
    return this.view.isInDefaultState();
  }

  onResetClicked() {
    const dialogRef = this.matDialog.open(ResetConfirmDialogComponent);

    const obs = dialogRef.afterClosed().subscribe(resetConfirmed => {
      if (resetConfirmed) {
        this.resetToDefault();
      }
      obs.unsubscribe();
    });
  }

  resetToDefault() {
    this.view.resetToDefault();
    for (const category of this.view.Categories) {
      category.Options = category.Options.map(opt => new Option(opt, opt.IsSelected));
    }

    this.panoService.updateSceneByView(this.view);

    this.disableResetButton = true;
    this.cdr.markForCheck();
  }

  handleViewSelect(item: MenuAccordionItemVM) {
    this.viewSelect.emit(item.Id);
  }

  handleTourSelect(item: MenuAccordionItemVM) {
    for (const _item of this.tourAccordion.ChildItems) {
      _item.IsSelected = _item.Id === item.Id;
    }

    const tour = this.pano.Tours.find(x => x.TourId === item.Id);
    if (tour.Views.length > 0) {
      const view = tour.Views.sort(sortByOrder)[0];
      this.view = view;

      this.viewSelect.emit(view.ViewId);
    }
  }

  @HostListener('window:keydown', ['$event'])
  onMessage = (keyEvent: KeyboardEvent) => {
    if (!keyEvent) {
      return;
    }

    if (keyEvent.key === 'ArrowDown' || keyEvent.key === 'ArrowRight') {
      this.goToNextOption();
    } else if (keyEvent.key === 'ArrowUp' || keyEvent.key === 'ArrowLeft') {
      this.goToPreviousOption();
    }
  };

  private goToNextOption = () => {
    for (const category of this.view.Categories) {
      const enabledOptionIndex = category.Options.findIndex(x => x.IsSelected);
      if (enabledOptionIndex + 1 < category.Options.length) {
        this.selectOption(category.Options[enabledOptionIndex + 1], category);
        return;
      } else {
        this.selectOption(category.Options[0], category);
      }
    }
  };

  private goToPreviousOption = () => {
    for (const category of this.view.Categories) {
      const enabledOptionIndex = category.Options.findIndex(x => x.IsSelected);
      if (enabledOptionIndex - 1 >= 0) {
        this.selectOption(category.Options[enabledOptionIndex - 1], category);
        return;
      } else {
        this.selectOption(category.Options[category.Options.length - 1], category);
      }
    }
  };
}
